import { gql } from "@apollo/client";

export const LOGIN_AUTH = gql`
  mutation ($email: String, $username: String, $password: String!) {
    tokenAuth(email: $email, username: $username, password: $password) {
      token
      refreshToken
      success
      errors
      user {
        id
        company {
          id
          name
          users {
            id
            username
            email
            role
            firstName
            lastName
          }
          vessels {
            id
            name
            mmsi
            state
            isActive
          }
        }
        email
        username
        role
        firstName
        lastName
      }
    }
  }
`;

export const GET_COMPANY_TRIPS = gql`
  query($isActive: Boolean, $minDate: DateTime, $company: String) {
    getCompanyTrips(isActive: $isActive, minDate: $minDate, company: $company) {
      id
      name
      isActive
      user {
        id
        username
        email
        firstName
        lastName
        company {
          name
        }
      }
      createdBy {
        username
        firstName
        lastName
      }
      departureDate
      origin {
        id
        name
        city
        geo
      }
      maxDraft
      maxHeight
      vessel {
        id
        mmsi
        name
        state
      }
      currentPosition
      positionUpdatedAt
      statuses {
        status
        reason
      }
      destinations {
        destination {
          id
          name
          city
          geo
        }
        arrivedAt
        legNumber
        eta
      }
    }
  }
`

export const EDIT_USER = gql`
  mutation ($email: String, $id: UUID!, $role: UserRoles, $firstName: String, $lastName: String, $username: String) {
    editUser(id: $id, email: $email, role: $role, firstName: $firstName, lastName: $lastName, username: $username) {
      user {
        id
        email
        role
        firstName
        lastName
        username
      }
    }
  }
`

export const EDIT_VESSEL = gql`
  mutation ($id: UUID!, $mmsi: String, $name: String, $state: VesselStates) {
    editVessel(id: $id, mmsi: $mmsi, name: $name, state: $state) {
      vessel {
        id
        mmsi
        name
        state
      }
    }
  }
`

export const DELETE_USER = gql`
  mutation ($id: UUID!) {
    deleteUser(id: $id) {
      success
    }
  }
`

export const DELETE_VESSEL = gql`
  mutation ($id: UUID!) {
    deleteVessel(id: $id) {
      success
    }
  }
`

export const DELETE_TRIP = gql`
  mutation ($id: UUID!) {
    deleteTrip(id: $id) {
      success
    }
  }
`

export const FORGET_PASSWORD_MUTATION = gql`
  mutation ($email: String!) {
    sendPasswordResetEmail(email: $email) {
      success
      errors
    }
  }
`;

export const GET_VESSELS = gql`
  query {
    getVessels {
      id
      name
      mmsi
      state
      isActive
    }
  }
`;

export const RESET_PASSWORD = gql`
  mutation ($token: String!, $newPassword1: String!, $newPassword2: String!) {
    passwordReset(
      token: $token
      newPassword1: $newPassword1
      newPassword2: $newPassword2
    ) {
      success
      errors
    }
  }
`;

export const CHANGE_PASSWORD = gql`
  mutation ($oldPassword: String!, $newPassword1: String!, $newPassword2: String!) {
    passwordChange(
      oldPassword: $oldPassword
      newPassword1: $newPassword1
      newPassword2: $newPassword2
    ) {
      success
      errors
    }
  }
`;

export const ADD_TRIP = gql`
  mutation (
    $name: String!
    $origin: Int!
    $destinations: [Int]!
    $vesselId: UUID!
    $departureDate: DateTime!
    $maxDraft: Float
    $maxHeight: Float
    $user: UUID
  ) {
    createTrip(
      name: $name
      origin: $origin
      destinations: $destinations
      vesselId: $vesselId
      departureDate: $departureDate
      maxDraft: $maxDraft
      maxHeight: $maxHeight
      user: $user
    ) {
      trip {
        id
        name
        origin {
          id
          name
          geo
          city
        }
        maxDraft
        maxHeight
        currentPosition
        positionUpdatedAt
        vessel {
          id
          company {
            id
            name
            address
            users {
              id
              username
              email
              role
            }
            vessels {
              id
              mmsi
              name
            }
          }
          mmsi
          name
        }
        departureDate
        createdAt
        isActive
        statuses {
          datetime
          reason
          status
          id
        }
        destinations {
          destination {
            id
            name
            geo
            city
          }
          arrivedAt
          eta
          trip {
            etaAlerts {
              eta
              message
            }
          }
          legNumber
        }
        user {
          id
          username
          email
          role
          firstName
          lastName
          company {
            name
          }
        }
        riverHeightAlerts {
          active
          id
          title
          message
          severity
          clearanceAtEta
          clearance
          dt
          station
          location
          mile
          sortOrder
          eta
        }

        bagAlerts {
          active
          id
          title
          message
          severity
          clearanceAtEta
          clearance
          dt
          bridgeId
          location
          mile
          sortOrder
          eta
        }

        msiAlerts {
          active
          id
          title
          message
          severity
          source
          noticeId
          startDate
          endDate
          mile
          location
          sortOrder
          eta
        }

        lockAlerts {
          active
          id
          title
          message
          severity
          lockName
          waterway
          queueLength
          avgDelay
          queueLength
          lockId
          mile
          location
          sortOrder
          eta
        }
      }
    }
  }
`;
export const CALCULATE_TRIP = gql`
  query ($origin: Int!, $destinations: [Int]!, $departureDate: DateTime!) {
    calculateTrip(
      departureDate: $departureDate
      destinations: $destinations
      origin: $origin
    ) {
      eta
    }
  }
`;

export const EDIT_TRIP = gql`
  mutation (
    $origin: Int!
    $destinations: [Int]!
    $departureDate: DateTime!
    $id: UUID!
    $name: String
    $vesselId: UUID
    $maxDraft: Float
    $maxHeight: Float
    $user: UUID
  ) {
    editTrip(
      destinations: $destinations
      id: $id
      name: $name
      origin: $origin
      vesselId: $vesselId
      departureDate: $departureDate
      maxDraft: $maxDraft
      maxHeight: $maxHeight
      user: $user
    ) {
      trip {
        id
        name
        origin {
          id
          name
          geo
          city
        }
        maxDraft
        maxHeight
        currentPosition
        positionUpdatedAt
        vessel {
          id
          company {
            id
            name
            address
            users {
              id
              username
              email
              role
            }
            vessels {
              id
              mmsi
              name
            }
          }
          mmsi
          name
        }
        departureDate
        createdAt
        isActive
        statuses {
          datetime
          reason
          status
          id
        }
        destinations {
          destination {
            id
            name
            geo
            city
          }
          arrivedAt
          eta
          trip {
            etaAlerts {
              eta
              message
            }
          }
          legNumber
        }
        user {
          id
          username
          email
          role
          firstName
          lastName
          company {
            name
          }
        }
        riverHeightAlerts {
          active
          id
          title
          message
          severity
          clearanceAtEta
          clearance
          dt
          station
          location
          mile
          sortOrder
          eta
        }

        bagAlerts {
          active
          id
          title
          message
          severity
          clearanceAtEta
          clearance
          dt
          bridgeId
          location
          mile
          sortOrder
          eta
        }

        msiAlerts {
          active
          id
          title
          message
          severity
          source
          noticeId
          startDate
          endDate
          mile
          location
          sortOrder
          eta
        }

        lockAlerts {
          active
          id
          title
          message
          severity
          lockName
          waterway
          queueLength
          avgDelay
          queueLength
          lockId
          mile
          location
          sortOrder
          eta
        }
      }
    }
  }
`;

export const CREATE_USER = gql`
  mutation (
    $companyId: UUID!, 
    $email: String!, 
    $firstName: String, 
    $lastName: String,
    $password1: String!,
    $password2: String!,
    $role: UserRoles,
    $username: String!
  ) {
    createUser(
      companyId: $companyId,
      email: $email,
      firstName: $firstName,
      lastName: $lastName,
      password1: $password1,
      password2: $password2,
      role: $role,
      username: $username
    ) {
      user {
        username
        id
        email
        role
        firstName
        lastName
      }
    }
  }
`

export const CREATE_VESSEL = gql`
  mutation (
    $companyId: UUID!,
    $mmsi: String!,
    $name: String,
    $state: VesselStates
  ) {
    createVessel(
      companyId: $companyId,
      mmsi: $mmsi,
      name: $name,
      state: $state
    ) {
      vessel {
        id
        mmsi
        name
        state
      }
    }
  }
`

export const GET_TRAFFIC = gql`
  query ($origin: Geometry!, $destinations: [Int]!) {
    getTraffic(origin: $origin, destinations: $destinations) {
      color
      overallAvg
      avgRatio
      id
    }
  }
`;

export const GET_COMPANY = gql`
  query ($id: UUID!) {
    getCompany(id: $id) {
      id
      name
      users {
        id
        email
        role
        firstName
        lastName
      }
      vessels {
        id
        mmsi
        name
        state
        isActive
      }
    }
  }
`

export const GET_COMPANIES = gql`
  query {
    getCompanies(name: "") {
      id
      name
      vessels {
        id
        mmsi
        name
        state
        isActive
      }
      users {
        id
        username
        email
        firstName
        lastName
        role
      }
    }
  }
`

export const REFRESH_TOKEN = gql`
  mutation ($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      token
      errors
      success
      payload
    }
  }
`;

export const CSV_DATA_DECKLOGS = gql`
  query ($tripId: UUID!) {
    getDecklogs(tripId: $tripId) {
      datetime
      lat
      long
      waterway
      mile
      description
    }
  }
`;

export const CSV_DATA_PREVOYAGE = gql`
  query ($tripId: UUID!) {
    getPrevoyagePlan(tripId: $tripId) {
      datetime
      lat
      long
      waterway
      mile
      description
      eta
      alertType
      severity
    }
  }
`;

export const GET_LOCATION = gql`
  query ($text: String) {
    getLocations(text: $text) {
      id
      facilityType
      name
      latitude
      longitude
      city
      waterway
      mile
      geo
    }
  }
`;

export const GET_USER = gql`
  query($id: UUID!) {
    getUser(id: $id) {
      username
      id
      email
      company{
        id
        name
        users {
          id
          username
          email
          role
          firstName
          lastName
        }
        vessels {
          id
          name
          mmsi
          state
        }
      }
      role
      firstName
      lastName
    }
  }
`
export const GET_USERS = gql`
  query($username: String) {
    getUsers(username: $username) {
      username
      id
      email
      role
      firstName
      lastName
    }
  }
`

export const CREATE_TEMPLATE = gql`
  mutation (
    $destinations: [DestinationInput]!
    $maxDraft: Float
    $maxHeight: Float
    $originId: Int!
    $templateName: String!
    $vesselId: UUID!
  ) {
    createTripTemplate(
      destinations: $destinations
      maxDraft: $maxDraft
      maxHeight: $maxHeight
      originId: $originId
      templateName: $templateName
      vesselId: $vesselId
    ) {
      tripTemplate {
        id
        createdAt
      }
    }
  }
`

export const UPDATE_TEMPLATE = gql`
  mutation (
    $destinations: [DestinationInput]!
    $id: UUID!
    $maxDraft: Float
    $maxHeight: Float
    $originId: Int
    $templateName: String
    $vesselId: UUID
  ) {
    updateTripTemplate(
      destinations: $destinations
      id: $id
      maxDraft: $maxDraft
      maxHeight: $maxHeight
      originId: $originId
      templateName: $templateName
      vesselId: $vesselId
    ) {
      tripTemplate {
        id
      }
    }
  }
`

export const DELETE_TEMPLATE = gql`
  mutation (
    $id: UUID!
  ) {
    deleteTripTemplate (id: $id) {
      success
    }
  }
`

export const GET_COMPANY_TEMPLATES = gql`
  query {
    getTripTemplatesByCompany {
      id
      templateName
      createdAt
      maxDraft
      maxHeight
      origin {
        id
        name
        city
        latitude
        longitude
        geo
      }
      vessel {
        id
        mmsi
        name
        state
      }
      createdBy {
        firstName
        lastName
        username
        id
      }
      destinations {
        id
        legNumber
        destination {
          id
          name
          city
          latitude
          longitude
          geo
        }
      }
    }
  }
`

export const GET_MILE_PATH = gql`
  query ($tripId: UUID!) {
    getMilePath(tripId: $tripId) {
      id
      waterway
      mile
      lat
      long
      distance
      name
    }
  }
`